import type { BalanceDto } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { formatQuantity } from '../lib/format';
import { Button, Card, CardBody, ErrorNote, Field, inputClass } from './ui';

export type StockActionMode = 'issue' | 'transfer' | 'adjust' | 'scrap';

type Mode = StockActionMode;

const MODE_LABELS: Record<Mode, string> = {
  issue: 'Issue out',
  transfer: 'Move',
  adjust: 'Adjust',
  scrap: 'Scrap',
};

const MODE_HINTS: Record<Mode, string> = {
  issue: 'Draws stock out of this location against a reason.',
  transfer: 'Moves stock to another location. The total on hand does not change.',
  adjust: 'A stock-count correction. Positive finds extra material, negative finds less.',
  scrap: 'A deliberate loss. A reason is mandatory, because nothing should vanish silently.',
};

/**
 * The four ways stock leaves or moves, all against one pre-selected balance so
 * the operator cannot quietly pick the wrong lot or location.
 */
export function StockActionPanel({
  balance,
  onDone,
  initialMode = 'transfer',
}: {
  balance: BalanceDto;
  onDone: () => void;
  /** Which operation to open on — the dashboard can ask for one directly. */
  initialMode?: Mode;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [quantity, setQuantity] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [reason, setReason] = useState('');

  const locations = useQuery({
    queryKey: ['locations', { limit: 200 }],
    queryFn: () => api.locations.list({ limit: 200 }),
  });

  const uom = balance.baseUom;
  const reasonRequired = mode === 'adjust' || mode === 'scrap';

  const submit = useMutation({
    // Deliberately returns nothing: the four operations have different result
    // shapes, and the panel only cares that it succeeded.
    mutationFn: async () => {
      const amount = quantity.trim();
      const note = reason.trim();

      switch (mode) {
        case 'issue':
          await api.inventory.issue({
            itemId: balance.itemId,
            lotId: balance.lotId,
            locationId: balance.locationId,
            quantity: amount,
            uom,
            ...(note ? { reason: note } : {}),
          });
          return;
        case 'transfer':
          await api.inventory.transfer({
            lotId: balance.lotId,
            fromLocationId: balance.locationId,
            toLocationId,
            quantity: amount,
            uom,
            ...(note ? { reason: note } : {}),
          });
          return;
        case 'adjust':
          await api.inventory.adjust({
            lotId: balance.lotId,
            locationId: balance.locationId,
            quantityDelta: amount,
            uom,
            reason: note,
          });
          return;
        case 'scrap':
          await api.inventory.scrap({
            lotId: balance.lotId,
            locationId: balance.locationId,
            quantity: amount,
            uom,
            reason: note,
          });
          return;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['lot'] });
      onDone();
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submit.mutate();
  }

  const available = formatQuantity(balance.quantity, uom);

  return (
    <Card className="mb-6 border-l-4 border-l-accent">
      <CardBody>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-bold text-ink">
            {MODE_LABELS[mode]}{' '}
            <span className="font-normal text-ink-muted">
              · {balance.itemCode} lot {balance.lotCode} · {balance.locationCode}
            </span>
          </h2>
          <span className="text-sm text-ink-body">
            On hand here: <strong>{available}</strong>
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(Object.keys(MODE_LABELS) as Mode[]).map((candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => {
                setMode(candidate);
                setQuantity('');
                setReason('');
              }}
              className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide transition ${
                candidate === mode
                  ? 'border-brand bg-brand-soft text-brand-deep'
                  : 'border-surface-border bg-white text-ink-body hover:border-brand'
              }`}
            >
              {MODE_LABELS[candidate]}
            </button>
          ))}
        </div>

        <p className="mt-3 text-sm font-light text-ink-body">{MODE_HINTS[mode]}</p>

        <form
          autoComplete="off"
          onSubmit={handleSubmit}
          className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Field
            label={mode === 'adjust' ? `Quantity change (${uom})` : `Quantity (${uom})`}
            hint={mode === 'adjust' ? 'Use a minus sign to reduce stock' : undefined}
          >
            <input
              className={inputClass}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder={mode === 'adjust' ? '-2.5' : '10'}
              inputMode="decimal"
              required
            />
          </Field>

          {mode === 'transfer' ? (
            <Field label="Move to">
              <select
                className={inputClass}
                value={toLocationId}
                onChange={(event) => setToLocationId(event.target.value)}
                required
              >
                <option value="">Select a destination…</option>
                {locations.data?.items
                  .filter((location) => location.id !== balance.locationId)
                  .map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.code} — {location.name}
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}

          <Field
            label="Reason"
            hint={reasonRequired ? 'Required' : 'Optional'}
          >
            <input
              className={inputClass}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required={reasonRequired}
              minLength={reasonRequired ? 3 : undefined}
              placeholder={reasonRequired ? 'Why did the count change?' : 'Optional note'}
            />
          </Field>

          <div className="sm:col-span-2 lg:col-span-3">
            {submit.error ? (
              <ErrorNote>
                {submit.error instanceof ApiError ? submit.error.message : 'Could not post the movement.'}
              </ErrorNote>
            ) : null}

            <div className="mt-3 flex gap-2">
              <Button type="submit" disabled={submit.isPending}>
                {submit.isPending ? 'Posting…' : MODE_LABELS[mode]}
              </Button>
              <Button tone="ghost" onClick={onDone}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
